/** @file This file contains utilities to handle background tasks and their result. */
import moment from 'moment';
import * as api from '@codegrade/api';
import { Maybe } from '@codegrade/maybe';
import client from '@/client';
import * as utils from '@/utils';
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export class TaskResult {
    id;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(id) {
        this.id = id;
        Object.freeze(this);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static fromServerData(data) {
        return new TaskResult(data.id);
    }
    /**
     * Polls a background job with a set interval, until its execution stops, and returns its result.
     *
     * @param waitTime Wait time in-between polling of the background job.
     * @returns The result of the task.
     */
    poll(waitTime = moment.duration(5, 's')) {
        let stop = false;
        /**
         * Performs a single poll of the the task and returns its result or a callback to be
         * executed after the wait time.
         *
         * @returns Either the result of the task or a callback that waits the set interval time and
         * re-executes the function.
         */
        const pollInner = () => {
            if (stop) {
                return utils.eitherAsyncUtils.rightFromValue(Maybe.empty());
            }
            return client.taskResult.get({ taskResultId: this.id }).chain(response => {
                const result = response.body;
                const state = result.state;
                switch (state) {
                    case 'finished':
                        return utils.eitherAsyncUtils.rightFromValue(result.result);
                    case 'crashed':
                    case 'failed':
                    case 'revoked':
                    case 'skipped': {
                        const error = result.result
                            .chain((err) => Maybe.fromEither(api.AnyError.decode(err)))
                            .orDefault(state);
                        return utils.eitherAsyncUtils.leftFromValue(new api.ApiResultError(error, new Response(undefined, { status: response.status }), [], error instanceof api.BaseError ? error.message : error, new Error(`Task failed with state: ${state}`)));
                    }
                    case 'started':
                    case 'not_started':
                        return utils.eitherAsyncUtils.sleep(waitTime).chain(pollInner);
                    default:
                        return utils.AssertionError.assertNever(state);
                }
            });
        };
        const poll = utils.eitherAsyncUtils
            .sleep(waitTime.clone().subtract(waitTime.asMilliseconds() / 2, 'milliseconds'))
            .chain(pollInner);
        return {
            result: poll.run(),
            stop: () => {
                stop = true;
            },
        };
    }
}
